import { css } from '@emotion/react'
import { Heading, HFlow, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { DateTime } from 'components/date'
import { Cns, Cpf } from 'components/label'
import { useMemo } from 'react'

import { ContentCardSection } from '../../soap/aside/components/ContentCardSection'
import { formatMotivoVisita } from '../formatter-prontuarioFamiliar'
import {
  ProntuarioFamiliarVisitaModel,
  recordVisitaDomiciliar,
  TipoVisitaDomiciliarEnum,
} from '../model-prontuarioFamiliar'

interface ProntuarioFamiliarVisitaDomiciliarCardProps {
  visita: ProntuarioFamiliarVisitaModel
}

export function ProntuarioFamiliarVisitaDomiciliarCard(props: ProntuarioFamiliarVisitaDomiciliarCardProps) {
  const { visita } = props
  const tipoVisita =
    visita?.tipoVisita in recordVisitaDomiciliar ? visita?.tipoVisita : TipoVisitaDomiciliarEnum.INDEFINIDA
  const visitaHeader = recordVisitaDomiciliar[tipoVisita]
  const profissionalNome = visita?.dimensaoProfissional?.nome ?? 'Profissional não informado'
  const cboNome = visita?.dimensaoCbo?.nome?.capitalize() ?? 'CBO não informado'
  const equipeNome = visita?.dimensaoEquipe?.nome ?? 'Equipe não informada'
  const equipeIne = visita?.dimensaoEquipe?.ine
  let documentoCidadaoVisitado: JSX.Element | string = '-'
  if (visita.cidadaoPec?.cpf) {
    documentoCidadaoVisitado = <Cpf value={visita.cidadaoPec.cpf} />
  } else if (visita.cidadaoPec?.cns) {
    documentoCidadaoVisitado = <Cns value={visita.cidadaoPec.cns} />
  }

  const theme = useTheme()
  const styles = createStyles(theme, visitaHeader.headerBarColor)

  const motivoVisitaFormatado = useMemo(
    () => formatMotivoVisita(visita.motivoVisita, visita.buscaAtiva, visita.controleAmbiental, visita.acompanhamento),
    [visita.motivoVisita, visita.buscaAtiva, visita.controleAmbiental, visita.acompanhamento]
  )

  return (
    <Box style={styles.box}>
      <HFlow hSpacing={0.5} style={styles.header} alignItems='center'>
        <img src={visitaHeader.icon} alt={visitaHeader.alt} />
        <Heading level={4}>{visitaHeader.title}</Heading>|
        <HFlow hSpacing={0.25}>
          <DateTime format='DD/MM/YYYY' value={visita.dataVisita} />({visita.turno?.descricao ?? 'Turno não informado'})
        </HFlow>
      </HFlow>
      <ContentCardSection>
        <VFlow vSpacing={1.5}>
          <VFlow vSpacing={0.5}>
            <HFlow hSpacing={0.25}>
              <Text fontWeight='bold'>Motivo da visita:</Text>
              <Text>{motivoVisitaFormatado}</Text>
            </HFlow>
            {visita.tipoVisita !== TipoVisitaDomiciliarEnum.FAMILIAR && (
              <HFlow hSpacing={0.25}>
                <Text fontWeight='bold'> Cidadão visitado: </Text>
                <Text>{visita.cidadaoPec?.nomeSocial?.titleCase() ?? visita.cidadaoPec?.nome?.titleCase()} - </Text>
                <Text>{documentoCidadaoVisitado}</Text>
              </HFlow>
            )}
            <Text
              style={css`
                word-break: break-word;
              `}
            >
              {visita.anotacoes ?? 'Sem anotação'}
            </Text>
          </VFlow>
          <HFlow hSpacing={0.25}>
            <Text fontWeight='bold'>Realizado por:</Text>
            <Text>
              {profissionalNome} | {cboNome} | {equipeNome}
              {equipeIne ? ` - ${equipeIne}` : ''}
            </Text>
          </HFlow>
        </VFlow>
      </ContentCardSection>
    </Box>
  )
}

const createStyles = (theme: Theme, borderColor: string) => ({
  box: css`
    border: 0.063rem solid ${theme.pallete.gray.c80};
    box-sizing: border-box;
    padding: 0;
    margin-top: 1rem;
  `,
  header: css`
    background: ${theme.pallete.gray.c90};
    padding: 0.25rem 0.5rem;
    border-radius: 2px;
    border-left: 0.5rem solid ${borderColor};
  `,
})
